import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {type ConnectionOperation, OPTIMISTIC_KEY, type SetFieldsOperation} from '@github-ui/optimistic-connection'
import {connectionMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {createLinkedBranchMutation as createLinkedBranchQueryId} from '@github-ui/server-persisted-queries'
import type {
  CreateLinkedBranchInput as ServerCreateLinkedBranchInput,
  CreateLinkedBranchMutationMutation,
} from '@github-ui/server-persisted-queries/types/createLinkedBranchMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useMemo} from 'react'

import {issueViewerSecondaryViewQueryKey} from '../components/fetch-issue-viewer-secondary-view'

export type CreateLinkedBranchInput = Omit<ServerCreateLinkedBranchInput, 'clientMutationId'>

type CreateLinkedBranchResponse = {
  readonly data?: CreateLinkedBranchMutationMutation | null
  readonly errors?: readonly unknown[] | null
}

type CreateLinkedBranchRoute = {
  owner: string
  repo: string
  number: number
}

const INVALID_RESPONSE_ERROR = 'Create linked branch response was invalid'

export async function fetchCreateLinkedBranch(
  {owner, repo, number}: CreateLinkedBranchRoute,
  input: CreateLinkedBranchInput,
): Promise<CreateLinkedBranchMutationMutation> {
  const {issueId: _routeBoundIssueId, ...routeSafeInput} = input
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${number}/linked_branches.json?queryId=${encodeURIComponent(
      createLinkedBranchQueryId,
    )}`,
    {
      method: 'POST',
      body: {input: routeSafeInput},
    },
  )

  if (!response.ok) throw new Error(`Create linked branch failed (${response.status})`)

  let json: CreateLinkedBranchResponse
  try {
    json = (await response.json()) as CreateLinkedBranchResponse
  } catch {
    throw new Error(INVALID_RESPONSE_ERROR)
  }

  const topLevelMessages = errorMessages(json.errors)
  if (topLevelMessages.length > 0) throw new Error(topLevelMessages.join(', '))
  if (!json.data) throw new Error(INVALID_RESPONSE_ERROR)

  const payload = json.data.createLinkedBranch
  if (!payload) throw new Error('Create linked branch payload was missing')

  const payloadMessages = errorMessages(payload.errors)
  if (payloadMessages.length > 0) {
    const duplicateMessage = ERRORS.branchAlreadyExists(input.name ?? '')
    if (payloadMessages.includes(duplicateMessage)) {
      const error = new Error(duplicateMessage)
      error.name = 'LinkedBranchExists'
      throw error
    }
    throw new Error(payloadMessages.join(', '))
  }

  if (!payload.linkedBranch) {
    const error = new Error('Linked branch not found')
    error.name = 'LinkedBranchNotFound'
    throw error
  }
  if (!payload.issue) throw new Error('Create linked branch issue was missing')

  return json.data
}

function errorMessages(errors: readonly unknown[] | null | undefined): string[] {
  if (!errors) return []

  return errors.flatMap(error => {
    if (!error || typeof error !== 'object' || !('message' in error)) return []
    return typeof error.message === 'string' && error.message.length > 0 ? [error.message] : []
  })
}

export const LINKED_BRANCHES_CONNECTION_PATH = ['linkedBranches']

export const PENDING_LINKED_BRANCH_PREFIX = 'pending-linked-branch:'

export function isPendingLinkedBranchId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith(PENDING_LINKED_BRANCH_PREFIX)
}

type AddLinkedBranchOperation = Extract<ConnectionOperation, {type: 'addToConnection'}>

type CreateLinkedBranchIssue = NonNullable<
  NonNullable<CreateLinkedBranchMutationMutation['createLinkedBranch']>['issue']
>
type ConfirmedLinkedBranchNode = NonNullable<NonNullable<CreateLinkedBranchIssue['linkedBranches']['nodes']>[number]>

export type CreateLinkedBranchRequest = {
  input: CreateLinkedBranchInput
  repositoryNameWithOwner: string
}

type CreateLinkedBranchVariables = {
  // Preserve the original input: an omitted repositoryId means the issue's repository.
  input: CreateLinkedBranchInput
  operation?: AddLinkedBranchOperation
}

export function prepareCreateLinkedBranch(request: CreateLinkedBranchRequest): CreateLinkedBranchVariables {
  return {
    input: request.input,
    operation: isFeatureEnabled('issues_optimistic_linked_branch_create')
      ? pendingLinkedBranchOperation(request)
      : undefined,
  }
}

function splitRefName(fullName: string): {prefix: string; name: string} {
  const prefix = 'refs/heads/'
  return fullName.startsWith(prefix) ? {prefix, name: fullName.slice(prefix.length)} : {prefix: '', name: fullName}
}

function pendingLinkedBranchOperation({
  input,
  repositoryNameWithOwner,
}: CreateLinkedBranchRequest): AddLinkedBranchOperation | undefined {
  if (!input.name || input.repositoryId == null) return undefined

  const temporaryId = `${PENDING_LINKED_BRANCH_PREFIX}${crypto.randomUUID()}`
  const {prefix, name} = splitRefName(input.name)

  return {
    type: 'addToConnection',
    connectionPath: LINKED_BRANCHES_CONNECTION_PATH,
    anchor: {at: 'end'},
    node: {
      id: temporaryId,
      [OPTIMISTIC_KEY]: temporaryId,
      ref: {
        __typename: 'Ref',
        id: `${temporaryId}:ref`,
        name,
        prefix,
        target: {__typename: 'Commit', id: `${temporaryId}:target`, oid: input.oid},
        repository: {
          id: String(input.repositoryId),
          nameWithOwner: repositoryNameWithOwner,
          defaultBranchRef: null,
        },
        associatedPullRequests: {totalCount: 0},
      },
    },
  }
}

function temporaryIdOf(operation: AddLinkedBranchOperation | undefined): string | undefined {
  const key = operation?.node[OPTIMISTIC_KEY]
  return typeof key === 'string' ? key : undefined
}

// Correlate the confirmed row with its placeholder to prevent duplication during settlement.
function confirmedLinkedBranchOperation(
  data: CreateLinkedBranchMutationMutation,
  temporaryId: string,
): AddLinkedBranchOperation | undefined {
  const payload = data.createLinkedBranch
  const createdId = payload?.linkedBranch?.id
  const node = payload?.issue?.linkedBranches?.nodes?.find(
    (candidate): candidate is ConfirmedLinkedBranchNode => candidate?.id === createdId,
  )
  if (!node) return undefined

  return {
    type: 'addToConnection',
    connectionPath: LINKED_BRANCHES_CONNECTION_PATH,
    anchor: {at: 'end'},
    node: {...node, [OPTIMISTIC_KEY]: temporaryId},
  }
}

export function useCreateLinkedBranchMutation({issueId, ...route}: CreateLinkedBranchRoute & {issueId: string}) {
  const queryClient = useQueryClient()
  const {owner, repo, number} = route
  const optimisticEnabled = isFeatureEnabled('issues_optimistic_linked_branch_create')

  const targets = useMemo(
    () => [{queryKey: issueViewerSecondaryViewQueryKey(owner, repo, number)}],
    [owner, repo, number],
  )
  // Connection keys let useOptimisticData collect pending branch operations.
  const mutationKey = useMemo(
    () =>
      optimisticEnabled
        ? connectionMutationKey(issueId, LINKED_BRANCHES_CONNECTION_PATH)
        : ['issue-viewer', 'create-linked-branch', {owner, repo, number}],
    [optimisticEnabled, issueId, owner, repo, number],
  )

  return useMutation({
    mutationKey,
    mutationFn: (variables: CreateLinkedBranchVariables) => fetchCreateLinkedBranch(route, variables.input),
    onSuccess: (data, variables) => {
      const temporaryId = temporaryIdOf(variables.operation)
      const confirmed = temporaryId ? confirmedLinkedBranchOperation(data, temporaryId) : undefined
      if (!confirmed) {
        // Use the full response when a created row cannot be correlated.
        return settleCreateLinkedBranchMutation({queryClient, route, data})
      }
      return settleConfirmedOperation({queryClient, targets, ownerId: issueId, operation: confirmed})
    },
  })
}

type QueryClient = ReturnType<typeof useQueryClient>

export async function settleCreateLinkedBranchMutation({
  queryClient,
  route,
  data,
}: {
  queryClient: QueryClient
  route: CreateLinkedBranchRoute
  data: CreateLinkedBranchMutationMutation
}) {
  const issue = data.createLinkedBranch?.issue
  if (!issue) throw new Error('Create linked branch issue was missing')

  const operation: SetFieldsOperation = {
    type: 'setFields',
    id: issue.id,
    fields: {linkedBranches: issue.linkedBranches},
  }

  await settleConfirmedOperation({
    queryClient,
    targets: [{queryKey: issueViewerSecondaryViewQueryKey(route.owner, route.repo, route.number)}],
    ownerId: issue.id,
    operation,
  })
}
